import { inject, Injectable } from "@angular/core";

import {
    HtmlDirectiveEvaluator,
    HtmlDirectiveEvaluatorSettings,
    HtmlExpression,
    HtmlExpressionType,
    HtmlForLoopDirective,
    HtmlForLoopIdentifiers,
    HtmlForLoopStructure,
    HtmlText,
} from "@main/reporting.v3/widget.html/models";
import {
    HtmlBaseExpressionsArgumentService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-argument.service";
import {
    HtmlBaseExpressionsEvaluatorService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-evaluator.service";


@Injectable()
export class HtmlForLoopDirectiveService implements HtmlDirectiveEvaluator {

    private argumentService: HtmlBaseExpressionsArgumentService = inject(HtmlBaseExpressionsArgumentService);
    private evaluatorService: HtmlBaseExpressionsEvaluatorService = inject(HtmlBaseExpressionsEvaluatorService);

    public readonly directive: string = "for";

    public async evaluate(directive: HtmlForLoopDirective, settings: HtmlDirectiveEvaluatorSettings): Promise<any> {
        if (!directive?.iterator || directive.name !== this.directive || !settings) return null;

        const block: any = await this.argumentService.evaluate(directive.block, settings);
        const body: string = this.argumentService.toString(block);

        // Ensure block content is passed and it is valid.
        if (!body?.length) return null;

        const [variable, data] = directive.iterator;

        // Ensure iterator variable name is passed and it is valid.
        if (!variable?.length) return null;

        // If the iterator data is a text expression, assume is a JSON list expression and parse it.
        // Otherwise, evaluate the expression and take the result as the iterator data value.
        const value: any = data.type === HtmlExpressionType.Text
            ? this.argumentService.toListString((data as HtmlText).text)
            : await this.evaluatorService.evaluate(data, settings);

        const values: any[] = Array.isArray(value) ? value : [value];

        // Ensure iterator values is passed and it is valid
        if (!values?.length) return null;

        const structure: HtmlForLoopStructure = {
            declarations: directive.declarations,
            variable: variable,
            values: values,
            body: body
        };

        return this.for(settings, structure);
    }

    public async for(settings: HtmlDirectiveEvaluatorSettings, structure: HtmlForLoopStructure): Promise<any> {
        try {
            if (!settings?.context || !structure?.values?.length || !structure?.body?.length) return null;

            const declarations: [string, HtmlExpression][] = Object.entries(structure.declarations ?? {});

            // Process each value in the loop.
            const results: string[] = Array(structure.values.length).fill(structure.body);

            for (let index: number = 0; index < structure.values.length; index++) {
                // Replace the main value variable occurrences with the value corresponding to this iteration.
                results[index] = this.replaceVariable(results[index], structure.variable, structure.values[index]);

                if (!declarations?.length) continue;

                // Clone the base settings and extend it to consider iteration variable.
                const extendedSettings = { ...settings };

                extendedSettings.context = { ...extendedSettings.context };
                extendedSettings.context.variables = { ...extendedSettings.context.variables };
                extendedSettings.context.variables[structure.variable] = structure.values[index];

                for (const [variable, data] of declarations) {

                    // If the declaration data is a text expression, assume is a string expression.
                    // Otherwise, evaluate the expression and take the result as the declaration data value.
                    const value: any = data.type === HtmlExpressionType.Text
                        ? this.toFixedValue((data as HtmlText).text, index, structure.values.length)
                        : await this.evaluatorService.evaluate(data, extendedSettings);

                    results[index] = this.replaceVariable(results[index], variable, value);
                }
            }

            // Return all iterations joined together.
            return results.join("");

        } catch (error) {
            return null;
        }
    }

    public replaceVariable(content: string, variable: string, value: any): string {
        if (!content || !variable) return content;

        // Create regex to match @variable as a whole word.
        const regex: RegExp = new RegExp(`(?<=\\W|^)@${variable}(?=\\W|$)`, "g");

        return content.replace(regex, this.argumentService.toString(value) ?? "");
    }

    public toFixedValue(value: string, index: number, total: number): string {
        switch (value) {
            case HtmlForLoopIdentifiers.Index:
                return String(index);

            case HtmlForLoopIdentifiers.Count:
                return String(total);

            case HtmlForLoopIdentifiers.First:
                return String(index === 0);

            case HtmlForLoopIdentifiers.Last:
                return String(index === total - 1);

            case HtmlForLoopIdentifiers.Even:
                return String(index % 2 === 0);

            case HtmlForLoopIdentifiers.Odd:
                return String(index % 2 === 1);
        }

        return value;
    }
}